import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useMemo, useState } from 'react';

type ShowableShow<TPayload> = (
  ...payload: TPayload extends undefined ? [] : [TPayload]
) => void;

type ShowableCommands<TPayload> = {
  hide: () => void;
  show: ShowableShow<TPayload>;
};

type ShowableShownProps<TPayload> = ShowableCommands<TPayload> & {
  shown: true;
  payload: TPayload;
};
type ShowableNotShownProps<TPayload> = ShowableCommands<TPayload> & {
  shown: false;
};

export type Showable<TPayload = undefined> =
  | ShowableShownProps<TPayload>
  | ShowableNotShownProps<TPayload>;

/**
 * `useShowable` is an abstraction over React components that can be opened and closed.
 */
export const useShowable = <TPayload = undefined>(): Showable<TPayload> => {
  const [payload, setPayload] = useState<TPayload | undefined>(undefined);

  const show: ShowableShow<TPayload> = useStableCallback((...nextPayload) => {
    // The only way an argument isn't passed is if `TPayload extends undefined`.
    setPayload(
      (nextPayload[0] === undefined ? {} : nextPayload[0]) as TPayload,
    );
  });

  const hide = useStableCallback(() => {
    setPayload(undefined);
  });

  return useMemo(
    () =>
      payload === undefined
        ? {
            hide,
            show,
            shown: false,
          }
        : {
            hide,
            payload,
            show,
            shown: true,
          },
    [payload],
  );
};
